import { AVAILABLE_SERVICE, Consultation } from './consultation'
import { Actionable, HealthNavigatorResponse, HealthResource, UserMessageFeedback } from './navigator'

export enum PostMessageType {
  NAVIGATOR_MESSAGE = 'NAVIGATOR_MESSAGE',
  CONSULTATION_MESSAGE = 'CONSULTATION_MESSAGE',
  NAVIGATOR_RECOMMENDED_ACTION_CLICK = 'NAVIGATOR_RECOMMENDED_ACTION_CLICK',
  NAVIGATOR_HEALTH_RESOURCE_CLICK = 'NAVIGATOR_HEALTH_RESOURCE_CLICK',
  NAVIGATOR_MARKDOWN_LINK_CLICK = 'NAVIGATOR_MARKDOWN_LINK_CLICK',
  NAVIGATOR_CONVERSATION_STARTED = 'NAVIGATOR_CONVERSATION_STARTED',
  NAVIGATOR_CONVERSATION_ENDED = 'NAVIGATOR_CONVERSATION_ENDED',
  CLOSE_CLICK = 'CLOSE_CLICK',
  NEW_NAVIGATOR_SESSION_CLICK = 'NEW_NAVIGATOR_SESSION_CLICK',
  NAVIGATOR_FINISHED = 'NAVIGATOR_FINISHED',
  END_CONSULTATION_CLICK = 'END_CONSULTATION_CLICK',
  NAVIGATOR_MESSAGE_FEEDBACK_SUBMITTED = 'NAVIGATOR_MESSAGE_FEEDBACK_SUBMITTED',
  NAVIGATOR_MESSAGE_FEEDBACK_DELETED = 'NAVIGATOR_MESSAGE_FEEDBACK_DELETED',
  NAVIGATOR_SESSION_FEEDBACK_SUBMITTED = 'NAVIGATOR_SESSION_FEEDBACK_SUBMITTED',
  CONSULTATION_CREATED = 'CONSULTATION_CREATED'
}

interface ConversationIdPayload {
  conversationId: string
}

interface NavigatorPostMessage {
  type: PostMessageType.NAVIGATOR_MESSAGE
  payload: HealthNavigatorResponse & ConversationIdPayload
}

interface ConsultationPostMessage {
  type: PostMessageType.CONSULTATION_MESSAGE
  payload: Consultation
}

interface NavigatorRecommendedActionClickPostMessage {
  type: PostMessageType.NAVIGATOR_RECOMMENDED_ACTION_CLICK
  payload: Actionable & ConversationIdPayload
}

interface NavigatorHealthResourceClickPostMessage {
  type: PostMessageType.NAVIGATOR_HEALTH_RESOURCE_CLICK
  payload: HealthResource & ConversationIdPayload
}

interface NavigatorMarkdownLinkClickPostMessage {
  type: PostMessageType.NAVIGATOR_MARKDOWN_LINK_CLICK
  payload: {
    href?: string
    children?: unknown
  } & ConversationIdPayload
}

interface CloseClickPostMessage {
  type: PostMessageType.CLOSE_CLICK
  payload: {
    consultationType: AVAILABLE_SERVICE
  }
}

interface NewNavigatorSessionClickPostMessage {
  type: PostMessageType.NEW_NAVIGATOR_SESSION_CLICK
  payload: ConversationIdPayload
}

interface NavigatorFinishedPostMessage {
  type: PostMessageType.NAVIGATOR_FINISHED
  payload: ConversationIdPayload
}

interface NavigatorConversationEndedPostMessage {
  type: PostMessageType.NAVIGATOR_CONVERSATION_ENDED
  payload: ConversationIdPayload
}

interface EndConsultationClickPostMessage {
  type: PostMessageType.END_CONSULTATION_CLICK
  payload: {
    consultationType: AVAILABLE_SERVICE
  }
}

interface NavigatorMessageFeedbackSubmittedPostMessage {
  type: PostMessageType.NAVIGATOR_MESSAGE_FEEDBACK_SUBMITTED
  payload: UserMessageFeedback & ConversationIdPayload & { messageId: string }
}

interface NavigatorMessageFeedbackDeletedPostMessage {
  type: PostMessageType.NAVIGATOR_MESSAGE_FEEDBACK_DELETED
  payload: ConversationIdPayload & { messageId: string }
}

interface NavigatorSessionFeedbackSubmittedPostMessage {
  type: PostMessageType.NAVIGATOR_SESSION_FEEDBACK_SUBMITTED
  payload: UserMessageFeedback & ConversationIdPayload
}

interface ConsultationCreatedPostMessage {
  type: PostMessageType.CONSULTATION_CREATED
  payload: {
    consultationId: string
    conversationId?: string
  }
}

export type PostMessage =
  | NavigatorPostMessage
  | ConsultationPostMessage
  | NavigatorRecommendedActionClickPostMessage
  | NavigatorHealthResourceClickPostMessage
  | NavigatorMarkdownLinkClickPostMessage
  | CloseClickPostMessage
  | NewNavigatorSessionClickPostMessage
  | NavigatorFinishedPostMessage
  | NavigatorConversationEndedPostMessage
  | EndConsultationClickPostMessage
  | NavigatorMessageFeedbackSubmittedPostMessage
  | NavigatorMessageFeedbackDeletedPostMessage
  | NavigatorSessionFeedbackSubmittedPostMessage
  | ConsultationCreatedPostMessage

export type PostMessagePayload =
  | (HealthNavigatorResponse & ConversationIdPayload)
  | Consultation
  | (HealthResource & ConversationIdPayload)
  | (Actionable & ConversationIdPayload)
  | NavigatorMarkdownLinkClickPostMessage['payload']
  | CloseClickPostMessage['payload']
  | NavigatorFinishedPostMessage['payload']
  | NavigatorMessageFeedbackSubmittedPostMessage['payload']
  | NavigatorMessageFeedbackDeletedPostMessage['payload']
  | NavigatorSessionFeedbackSubmittedPostMessage['payload']
  | ConsultationCreatedPostMessage['payload']
  | ConversationIdPayload
  | EndConsultationClickPostMessage['payload']
